import React, {useCallback, useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import {
  fetchTalentBridgeAllLocations,
  talentBridgeContants,
  extractDate,
} from './utils';

const ORANGE = '#E85E00';
const INK = '#1f2430';
const SHADOW =
  '0 18px 40px -12px rgba(20,25,40,.35),0 4px 12px rgba(20,25,40,.12)';

// True when today falls within the drive window "DD/MM/YYYY - DD/MM/YYYY".
const isDriveLive = (eventDates?: string): boolean => {
  if (!eventDates) return false;
  const [startStr, endStr] = eventDates.split(' - ');
  if (!startStr || !endStr) return false;
  const [d1, m1, y1] = startStr.split('/').map(Number);
  const [d2, m2, y2] = endStr.split('/').map(Number);
  const startDate = new Date(y1, m1 - 1, d1);
  const endDate = new Date(y2, m2 - 1, d2);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today >= startDate && today <= endDate;
};

type TalentBridgePromoSliderProps = {
  // 'slider' = fixed docked card (desktop); 'banner' = inline full-width banner
  variant?: 'slider' | 'banner';
};

const TalentBridgePromoSlider: React.FC<TalentBridgePromoSliderProps> = ({
  variant = 'slider',
}) => {
  const [city, setCity] = useState<string>('');
  const [eventDates, setEventDates] = useState<string | null>(null);
  const [driveText, setDriveText] = useState<string>('');
  const [driveDate, setDriveDate] = useState<string>('');
  const [driveVenue, setDriveVenue] = useState<string>('');
  const [displayDate, setDisplayDate] = useState<string>('');
  const [driveCta, setDriveCta] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // view state (mirrors the reference HTML's `in` / `exit-down` classes)
  const [promoIn, setPromoIn] = useState(false);
  const [promoExit, setPromoExit] = useState(false);
  const [miniIn, setMiniIn] = useState(false);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const obj = await fetchTalentBridgeAllLocations();
        // Pick the city whose drive is LIVE today (showBanner + date window),
        // so the "Apply" link opens that location's Talent Bridge page.
        let activeCityKey = '';
        for (const cityKey in obj) {
          const data = obj[cityKey];
          if (data?.showBanner === 'true' && isDriveLive(data?.eventDates)) {
            activeCityKey = cityKey;
            break;
          }
        }
        if (activeCityKey) {
          const data = obj[activeCityKey];
          setCity(activeCityKey);
          setDriveText(data?.text || '');
          setDriveDate(data?.date || '');
          setDriveVenue(data?.venue || '');
          setDisplayDate(extractDate(data?.date || '', data?.month || ''));
          setDriveCta(data?.ctaText || '');
          setEventDates(data?.eventDates || '');
        }
      } catch (err) {
        console.error('Error fetching drive banner data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBanner();
  }, []);

  // Only show while the selected drive is live.
  const showBanner = isDriveLive(eventDates || undefined);

  // Entrance: slide the promo in shortly after it becomes eligible
  useEffect(() => {
    if (!showBanner) return undefined;
    const timer = setTimeout(() => setPromoIn(true), 700);
    return () => clearTimeout(timer);
  }, [showBanner]);

  const closePromo = useCallback(() => {
    setPromoIn(false);
    setPromoExit(true);
    // let the promo slide down before the pill pops up
    setTimeout(() => setMiniIn(true), 180);
  }, []);

  const openPromo = useCallback(() => {
    setMiniIn(false);
    setPromoExit(false);
    // reset to the off-screen-left base state, then replay the slide-in
    setPromoIn(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setPromoIn(true));
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && promoIn) closePromo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [promoIn, closePromo]);

  // Skeleton while the drive data is loading (banner variant only), so the
  // mobile jobfeed layout doesn't jump when the real banner resolves.
  if (variant === 'banner' && loading) {
    return (
      <Box
        aria-hidden="true"
        sx={{
          overflow: 'hidden',
          background:
            'linear-gradient(135deg,#FFF4E8 0%,#FDEAD8 55%,#FBE0C7 100%)',
          p: '8px 0 10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Skeleton
          variant="text"
          width={150}
          height={18}
          sx={{bgcolor: 'rgba(232,94,0,0.12)'}}
        />
        <Skeleton
          variant="text"
          width={200}
          height={14}
          sx={{bgcolor: 'rgba(232,94,0,0.1)'}}
        />
        <Skeleton
          variant="rounded"
          width={120}
          height={28}
          sx={{borderRadius: 999, bgcolor: 'rgba(232,94,0,0.18)'}}
        />
      </Box>
    );
  }

  if (!showBanner) return null;

  // Redirect to the live location's Talent Bridge detail page (ref: bridge_online)
  const inviteHref = `/${talentBridgeContants.redirectionUrl_temp}/${city}?ref=${talentBridgeContants.refCode_temp}`;

  // Inline full-width banner (used on mobile jobfeed, below the Download App banner)
  if (variant === 'banner') {
    const sparkles = [
      {top: '18%', left: '8%', delay: '0s', duration: '5.5s'},
      {top: '60%', left: '22%', delay: '.8s', duration: '6.5s'},
      {top: '26%', right: '12%', delay: '1.4s', duration: '5s'},
      {top: '66%', right: '26%', delay: '2.1s', duration: '7s'},
    ];

    return (
      <Box
        role="region"
        aria-label="Talent Bridge promotion"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 0,
          isolation: 'isolate',
          p: '8px 0 10px',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          '@keyframes tbWash': {
            '0%, 100%': {backgroundPosition: '0% 50%'},
            '50%': {backgroundPosition: '100% 50%'},
          },
          '@keyframes tbGlow': {
            '0%, 100%': {
              opacity: 0.55,
              transform: 'translateX(-50%) scale(1)',
            },
            '50%': {opacity: 1, transform: 'translateX(-50%) scale(1.12)'},
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: -3,
            background:
              'linear-gradient(100deg,#FFF4E8 0%,#FDEAD8 34%,#FBE0C7 58%,#FFF4E8 100%)',
            backgroundSize: '220% 100%',
            animation: 'tbWash 14s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            zIndex: -2,
            top: '-60%',
            left: '50%',
            width: '60%',
            height: '220%',
            transform: 'translateX(-50%)',
            background:
              'radial-gradient(closest-side, rgba(255,171,94,.35), rgba(255,171,94,0) 70%)',
            animation: 'tbGlow 9s ease-in-out infinite',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '&::before': {animation: 'none', backgroundPosition: '50% 50%'},
            '&::after': {animation: 'none', opacity: 0.7},
          },
        }}
      >
        {/* diagonal shine sweep */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            pointerEvents: 'none',
            overflow: 'hidden',
            '@keyframes tbSweep': {
              '0%': {left: '-30%'},
              '55%, 100%': {left: '130%'},
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-40%',
              bottom: '-40%',
              left: '-30%',
              width: '22%',
              transform: 'skewX(-18deg)',
              background:
                'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.65) 50%, rgba(255,255,255,0) 100%)',
              animation: 'tbSweep 7s ease-in-out infinite',
            },
            '@media (prefers-reduced-motion: reduce)': {
              '&::before': {animation: 'none'},
            },
          }}
        />

        {/* drifting sparkles */}
        {sparkles.map((spark, i) => (
          <Box
            key={i}
            aria-hidden="true"
            sx={{
              position: 'absolute',
              zIndex: -1,
              fontSize: 11,
              lineHeight: 1,
              color: '#EFA85A',
              opacity: 0,
              pointerEvents: 'none',
              top: spark.top,
              left: spark.left,
              right: spark.right,
              '@keyframes tbTwinkle': {
                '0%, 100%': {opacity: 0, transform: 'scale(.6)'},
                '45%': {opacity: 0.9, transform: 'scale(1)'},
              },
              animation: `tbTwinkle ${spark.duration} ease-in-out ${spark.delay} infinite`,
              '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
            }}
          >
            {i % 2 === 0 ? '✦' : '✧'}
          </Box>
        ))}

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{fontSize: 15, fontWeight: 800, color: INK, lineHeight: 1.15}}
          >
            Hirist Talent Bridge
          </Typography>

          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Box
              aria-hidden="true"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: ORANGE,
                flex: '0 0 auto',
                animation: 'tbPulse 2.2s ease-out infinite',
                '@keyframes tbPulse': {
                  '0%': {boxShadow: '0 0 0 0 rgba(232,94,0,.5)'},
                  '70%': {boxShadow: '0 0 0 7px rgba(232,94,0,0)'},
                  '100%': {boxShadow: '0 0 0 0 rgba(232,94,0,0)'},
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            />
            <Typography sx={{fontSize: 12, fontWeight: 500, color: INK}}>
              Live Hiring Drive
              {displayDate ? (
                <>
                  {' on '}
                  <Box component="span" sx={{fontWeight: 800}}>
                    {displayDate}
                  </Box>
                </>
              ) : (
                ''
              )}
            </Typography>
          </Box>

          <Box
            component="a"
            href={inviteHref}
            sx={{
              mt: 0.25,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 2.5,
              py: 0.6,
              borderRadius: 999,
              background: 'linear-gradient(135deg,#F5852A,#E85E00 55%,#C64E00)',
              backgroundSize: '180% 180%',
              color: '#fff',
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: '.5px',
              textTransform: 'uppercase',
              textDecoration: 'none',
              boxShadow: '0 6px 14px -6px rgba(232,94,0,.5)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform .18s ease, box-shadow .18s ease',
              animation: 'tbBtnShift 6s ease-in-out infinite',
              '@keyframes tbBtnShift': {
                '0%, 100%': {backgroundPosition: '0% 50%'},
                '50%': {backgroundPosition: '100% 50%'},
              },
              '@keyframes tbBtnShine': {
                '0%': {left: '-120%'},
                '60%, 100%': {left: '130%'},
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-120%',
                width: '70%',
                height: '100%',
                background:
                  'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.45), rgba(255,255,255,0))',
                transform: 'skewX(-20deg)',
                animation: 'tbBtnShine 5.5s ease-in-out 1s infinite',
              },
              '& svg': {transition: 'transform .18s ease'},
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 8px 18px -6px rgba(232,94,0,.6)',
              },
              '&:hover svg': {transform: 'translateX(2px)'},
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                '&::after': {animation: 'none'},
              },
            }}
          >
            {driveCta || 'Register Now'}
            <Box component="span" aria-hidden="true" sx={{display: 'flex'}}>
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 12h14M13 6l6 6-6 6" />
              </svg>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const promoClassName = `${promoIn ? 'in' : ''} ${
    promoExit ? 'exit-down' : ''
  }`.trim();

  // Split the dynamic tagline so the last word renders in the accent colour,
  // e.g. "New Age Tech & Data Roles" -> "NEW AGE TECH & DATA" + "ROLES"
  const subheading = (driveText || '').trim().toUpperCase();
  const subLastSpace = subheading.lastIndexOf(' ');
  const subLead = subLastSpace === -1 ? '' : subheading.slice(0, subLastSpace);
  const subTail =
    subLastSpace === -1 ? subheading : subheading.slice(subLastSpace + 1);

  return (
    <>
      {/* full docked banner (slides in from the left) */}
      <Box
        className={promoClassName}
        role="dialog"
        aria-label="Talent Bridge promotion"
        sx={theme => ({
          position: 'fixed',
          left: 24,
          bottom: 24,
          width: '360px',
          zIndex: 1200,
          transform: 'translateX(calc(-100% - 48px))',
          opacity: 0,
          transition:
            'transform .55s cubic-bezier(.22,.9,.3,1), opacity .35s ease',
          // keep text crisp: don't leave the card on a promoted GPU layer,
          // which softens font rendering after the slide-in finishes
          backfaceVisibility: 'hidden',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          '&.in': {transform: 'translateX(0)', opacity: 1},
          '&.exit-down': {
            transform: 'translateY(140%)',
            opacity: 0,
            transition: 'transform .4s ease-in, opacity .3s ease',
          },
          [theme.breakpoints.down('sm')]: {
            left: 16,
            bottom: 16,
            width: 'calc(100vw - 32px)',
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'opacity .2s ease',
            transform: 'none',
            '&.in': {transform: 'none'},
          },
        })}
      >
        <Box
          sx={{
            position: 'relative',
            borderRadius: '18px',
            overflow: 'hidden',
            boxShadow: SHADOW,
            background: '#fff',
            border: '3px solid #fff',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={closePromo}
            aria-label="Close promotion"
            title="Close"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 30,
              height: 30,
              border: 'none',
              borderRadius: '50%',
              background: 'rgba(255,255,255,.92)',
              color: INK,
              boxShadow: '0 3px 10px rgba(20,25,40,.22)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform .18s ease, background .18s ease',
              zIndex: 2,
              padding: 0,
              '&:hover': {background: '#fff', transform: 'scale(1.08)'},
              '&:active': {transform: 'scale(.94)'},
              '& svg': {
                width: 14,
                height: 14,
                stroke: 'currentColor',
                strokeWidth: 2.4,
                fill: 'none',
              },
            }}
          >
            <svg viewBox="0 0 24 24">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </Box>

          <Box
            component="a"
            href={inviteHref}
            aria-label={`Hirist Talent Bridge is back${
              driveDate ? `, ${driveDate}` : ''
            }${driveVenue ? ` at ${driveVenue}` : ''}. Apply now.`}
            sx={{
              position: 'relative',
              display: 'block',
              textDecoration: 'none',
              overflow: 'hidden',
              p: {xxs: '12px 14px 14px', sm: '14px 16px 16px'},
              background:
                'linear-gradient(135deg,#FFF4E8 0%,#FDEAD8 55%,#FBE0C7 100%)',
              // soft diagonal sheen, like the reference creative
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(115deg, transparent 40%, rgba(255,255,255,.22) 50%, transparent 60%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Box sx={{position: 'relative'}}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 0.5,
                }}
              >
                <Box
                  component="img"
                  src="https://hirist-assets.s3.ap-south-1.amazonaws.com/hirist-talent-bridge.webp"
                  alt="Hirist Talent Bridge"
                  sx={{height: 20, width: 'auto', display: 'block'}}
                />
              </Box>

              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: {xxs: 18, sm: 19},
                  fontWeight: 900,
                  lineHeight: 1.05,
                  textTransform: 'uppercase',
                  letterSpacing: '.2px',
                }}
              >
                <Box component="span" sx={{color: ORANGE}}>
                  Talent Bridge{' '}
                </Box>
                <Box component="span" sx={{color: INK}}>
                  is Back
                </Box>
              </Typography>

              {subheading && (
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontSize: {xxs: 10.5, sm: 11},
                    fontWeight: 800,
                    letterSpacing: '.4px',
                    textTransform: 'uppercase',
                    mt: 0.25,
                    color: INK,
                  }}
                >
                  {subLead}
                  {subLead ? ' ' : ''}
                  <Box component="span" sx={{color: ORANGE}}>
                    {subTail}
                  </Box>
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'center',
                  columnGap: {xxs: 1.25, sm: 1.75},
                  rowGap: 0.5,
                  mt: 1,
                }}
              >
                {driveDate && (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Box
                      aria-hidden="true"
                      sx={{display: 'flex', color: ORANGE}}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <rect x="3" y="4.5" width="18" height="16" rx="3" />
                        <path d="M3 9h18" />
                        <path d="M8 3v3M16 3v3" strokeLinecap="round" />
                      </svg>
                    </Box>
                    <Typography
                      sx={{fontSize: 11.5, fontWeight: 600, color: INK}}
                    >
                      {driveDate}
                    </Typography>
                  </Box>
                )}
                {driveVenue && (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Box
                      aria-hidden="true"
                      sx={{display: 'flex', color: ORANGE}}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <path d="M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11Z" />
                        <circle cx="12" cy="10" r="2.5" />
                      </svg>
                    </Box>
                    <Typography
                      sx={{fontSize: 11.5, fontWeight: 600, color: INK}}
                    >
                      {driveVenue}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{display: 'flex', justifyContent: 'center', mt: 1.25}}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2.75,
                    py: 0.75,
                    borderRadius: 999,
                    background:
                      'linear-gradient(180deg,#F58220 0%,#E85E00 100%)',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: '.6px',
                    textTransform: 'uppercase',
                    boxShadow: '0 8px 18px -6px rgba(232,94,0,.55)',
                    transition: 'transform .18s ease, box-shadow .18s ease',
                  }}
                >
                  Apply Now
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* minimized pill, bottom-middle */}
      <Box
        component="button"
        type="button"
        className={miniIn ? 'in' : ''}
        onClick={openPromo}
        aria-label="Reopen Talent Bridge promotion"
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 20,
          transform: 'translateX(-50%) translateY(160%)',
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 16px 8px 8px',
          background: '#fff',
          borderRadius: 999,
          boxShadow: SHADOW,
          cursor: 'pointer',
          border: '1px solid rgba(232,94,0,.18)',
          transition:
            'transform .5s cubic-bezier(.22,.9,.3,1), box-shadow .2s ease',
          maxWidth: 'calc(100vw - 32px)',
          '&.in': {transform: 'translateX(-50%) translateY(0)'},
          '&:hover': {
            boxShadow:
              '0 22px 46px -14px rgba(232,94,0,.4),0 4px 12px rgba(20,25,40,.14)',
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'opacity .2s ease',
            transform: 'translateX(-50%)',
            '&.in': {transform: 'translateX(-50%)'},
          },
        }}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(135deg,#FFE6D2,#FFD3B0)',
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'inset 0 0 0 1px rgba(232,94,0,.16)',
            '& svg': {
              width: 21,
              height: 21,
              stroke: ORANGE,
              strokeWidth: 1.9,
              fill: 'none',
            },
          }}
        >
          <svg viewBox="0 0 24 24">
            <rect x="3" y="8" width="18" height="12" rx="2.2" />
            <path d="M8 8V6.4A2.4 2.4 0 0 1 10.4 4h3.2A2.4 2.4 0 0 1 16 6.4V8" />
            <path d="M3 13h18" strokeLinecap="round" />
          </svg>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            lineHeight: 1.15,
            textAlign: 'left',
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: INK,
              whiteSpace: 'nowrap',
            }}
          >
            Talent Bridge is back
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: ORANGE,
              whiteSpace: 'nowrap',
            }}
          >
            Tech &amp; data roles · Apply
          </Typography>
        </Box>
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: ORANGE,
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '2px',
            '& svg': {
              width: 13,
              height: 13,
              stroke: '#fff',
              strokeWidth: 2.6,
              fill: 'none',
            },
          }}
        >
          <svg viewBox="0 0 24 24">
            <path
              d="M6 15l6-6 6 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      </Box>
    </>
  );
};

export default TalentBridgePromoSlider;
